import { AddManualLiabilityData } from '../models/add-manual-liability-data.model';
import { EditLiabilityData } from '../models/edit-liability-data.model';
import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { LoadingAction } from 'src/app/shared/models/loading-action.enum';
import { CustomerBiller } from '../../billers/models/model';
import { ApiService } from '../../core/services/api.service';
import { getCustomEvent } from '../../core/utils/http-request-utils';
import { DueMonth } from '../models/due-month.model';
import { LiabilityAutomaticPaymentEnabled } from '../models/liability-automatic-payment-enabled.model';
import { ISavedLiability, Liability } from '../models/liability.model';
import { SearchLiabilities } from '../models/search-liabilities.model';
import { AutomaticReadingResultData } from '../models/automatic-reading-result-data';
import { AddLiabilityScanData } from '../models/add-liability-scan-data.model';

@Injectable({
  providedIn: 'root',
})
export class LiabilityApiService {
  private NAMESPACE = 'liability';

  constructor(private api: ApiService) {}

  public getLiabilities(
    startDate: string,
    endDate: string,
    tagIds: string[] = [],
    subscribe: boolean = false,
  ): Observable<SearchLiabilities> {
    return this.api.get<SearchLiabilities>('/liabilities', { startDate, endDate, tagId: tagIds }, subscribe);
  }

  public getLiabilityWithSubscription(liabilityId: string): Observable<Liability> {
    return this.api.get<Liability>('/liabilities/' + liabilityId, null, true, 'liability', ([data, socketFrame]) =>
      socketFrame && socketFrame.id === data.id ? socketFrame : data,
    );
  }

  public getDueMonths(tagIds: string[] = []): Observable<DueMonth[]> {
    return this.api.get<DueMonth[]>(`/liabilities/due-months`, { tagId: tagIds });
  }

  public uploadDocumentsForAutomaticReading(files: File[]): Observable<any> {
    return from(this.api.uploadFiles(`/liabilities/automatic-reading`, files));
  }

  public addManualLiabilityFromFile(file: File): Observable<any> {
    return from(this.api.uploadFile(`/liabilities`, file));
  }

  public getAutomaticReadingResult(automaticReadingId: string): Observable<AutomaticReadingResultData> {
    return this.api.get(`/liabilities/automatic-reading/${automaticReadingId}`);
  }

  public isVerified(id: string): Observable<boolean> {
    return this.api.get(`/liabilities/verification/${id}`);
  }

  public getAutomaticReadingFileUrl(automaticReadingId: string): Observable<string> {
    return this.api.get(`/liabilities/automatic-reading/${automaticReadingId}/preview`);
  }

  public addLiabilityFromAutomaticReading(id: string, addLiabilityScanData: AddLiabilityScanData): Observable<string> {
    return this.api.post(`/liabilities/automatic-reading/${id}`, addLiabilityScanData);
  }

  public deleteAutomaticReading(id: string): Observable<string> {
    return this.api.delete(`/liabilities/automatic-reading/${id}`);
  }

  public updateManualLiabilities(customerBillerId: string, data: AddManualLiabilityData): Observable<any> {
    return this.api.post(`/customerBillers/${customerBillerId}/manual/liabilities`, data);
  }

  public saveManualLiabilities(data: AddManualLiabilityData): Observable<ISavedLiability> {
    return this.api.post(`/liabilities/manual`, data);
  }

  public getCustomerBillersWithManualSource(): Observable<CustomerBiller[]> {
    return this.api.get<CustomerBiller[]>('/customerBillers/manual');
  }

  public downloadPdf(liabilityId: string): Observable<string> {
    return this.api.get<string>(`/liabilities/${liabilityId}/documents/link`);
  }

  public enableAutoPayment(liabilityId: string): Observable<LiabilityAutomaticPaymentEnabled> {
    return this.api.put<LiabilityAutomaticPaymentEnabled>(`/liabilities/${liabilityId}/automatic-payment/enabled`);
  }

  public disableAutoPayment(liabilityId: string): Observable<Liability> {
    const param = getCustomEvent(LoadingAction.CANCEL_AUTO_PAYMENT, this.NAMESPACE, liabilityId);
    return this.api.put(`/liabilities/${liabilityId}/automatic-payment/disabled`, null, param);
  }

  public markAsPaid(liabilityId: string): Observable<Liability[]> {
    const param = getCustomEvent(LoadingAction.MARK_AS_PAID, this.NAMESPACE, liabilityId);
    return this.api.post<Liability[]>('/liabilities/status/paid', { liabilityIds: [liabilityId] }, { params: param });
  }

  public markAsNotPaid(liabilityId: string): Observable<Liability[]> {
    const param = getCustomEvent(LoadingAction.MARK_AS_NOT_PAID, this.NAMESPACE, liabilityId);
    return this.api.post<Liability[]>('/liabilities/status/not-paid', { liabilityIds: [liabilityId] }, { params: param });
  }

  public hide(liabilityId: string): Observable<any> {
    return this.api.put(`/liabilities/${liabilityId}/hidden`);
  }

  public show(liabilityId: string): Observable<any> {
    return this.api.put(`/liabilities/${liabilityId}/shown`);
  }

  public updateDescription(liabilityId: string, description: string): Observable<any> {
    return this.api.put(`/liabilities/${liabilityId}/description`, { description });
  }

  public edit(editLiabilityData: EditLiabilityData, liabilityId: string): Observable<any> {
    const param = getCustomEvent(LoadingAction.EDIT_LIABILITY, this.NAMESPACE, liabilityId);
    return this.api.put(`/liabilities/${liabilityId}`, editLiabilityData, param);
  }

  public updateAmount(liabilityId: string, amount: number): Observable<Liability> {
    const param = getCustomEvent(LoadingAction.EDIT_AMOUNT, this.NAMESPACE, liabilityId);
    return this.api.put(`/liabilities/${liabilityId}/amount`, amount, param);
  }

  public remindAboutPayment(liabilityId: string): Observable<any> {
    const param = getCustomEvent(LoadingAction.REMIND_ABOUT_DELEGATED_PAYMENT, this.NAMESPACE, liabilityId);
    return this.api.post(`/liabilities/${liabilityId}/reminder`, null, { params: param });
  }
}
